import {
  fetchMe,
  fetchSites,
  resolveApp,
} from '@/modules/Workspaces/KnowledgeHub/Integrations/SharePoint/apiUtils'

const PERSONAL_DOMAINS = ['outlook.com', 'hotmail.com', 'live.com']

/**
 * Resolves the given token's type (work or personal account)
 * SharePoint only works with work accounbts
 */
export const getValidatedTokenType = async (token: string): Promise<'personal' | 'work'> => {
  const data = await fetchMe(token)
  const isPersonal = PERSONAL_DOMAINS.some((domain) => data.userPrincipalName.includes(domain))
  return isPersonal ? 'personal' : 'work'
}

/**
 * Gets a SharePoint token using MSAL browser authentication
 */
export const getMsalToken = async (command: { resource: string }): Promise<string> => {
  const app = await resolveApp()
  const scopes = [`${command.resource}/.default`]
  const authParams = { scopes }

  // First try to get token silently
  try {
    const silentResult = await app.acquireTokenSilent(authParams)
    return silentResult.accessToken
  } catch {
    // If silent acquisition fails, try interactive popup
    const popupResult = await app.acquireTokenPopup(authParams)
    if (popupResult.account) {
      app.setActiveAccount(popupResult.account)
    }
    return popupResult.accessToken
  }
}

/**
 * Gets tenant information by querying Microsoft Graph API for accessible SharePoint sites
 */
const getTenantInfo = async (accessToken: string) => {
  const { value: sharePointSites } = await fetchSites(accessToken)

  if (sharePointSites.length === 0) {
    throw new Error('No accessible SharePoint sites found for this user')
  }

  // Use the first SharePoint site to determine the tenant URL
  const firstSite = sharePointSites[0]
  const sharePointUrl = firstSite.webUrl.includes('/sites/')
    ? firstSite.webUrl.split('/sites/')[0]
    : firstSite.webUrl

  return {
    sharePointUrl,
    oneDriveUrl: sharePointUrl.replace('.sharepoint.com', '-my.sharepoint.com'),
    sharePointSites,
  }
}

/**
 * Starts a connection with SharePoint on Go backend and picks files using Microsoft File Picker v8.
 * Creates a SharePoint file picker using Microsoft's official File Picker v8
 * Based on: https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers/?view=odsp-graph-online
 */
export const getFilePickerUrl = async (params: {
  accessToken: string
  channelId: string
  maxItems: number
}): Promise<string> => {
  const { accessToken, channelId, maxItems } = params
  const tenantInfo = await getTenantInfo(accessToken)

  const pickerOptions = {
    sdk: '8.0',
    entry: { sharePoint: {} },
    // Applications must pass this empty `authentication` option in order to obtain details item data
    authentication: {},
    messaging: { origin: window.location.origin, channelId },
    typesAndSources: { mode: 'files' },
    selection: { mode: maxItems === 1 ? 'single' : 'multiple', maximumCount: maxItems },
  }

  const queryString = new URLSearchParams({
    filePicker: JSON.stringify(pickerOptions),
    locale: 'en-us',
  })

  return `${tenantInfo.sharePointUrl}/_layouts/15/FilePicker.aspx?${queryString}`
}
